import LogoFerinaCura from "@/assets/logos/Header.svg";
import { isVereinAreaPath } from "@/constants/routes";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import {
  DrawerContentComponentProps,
  DrawerContentScrollView,
} from "@react-navigation/drawer";
import { DrawerActions } from "@react-navigation/native";
import { useOptionalFinderMode } from "@/ui/finder/FinderModeContext";
import { finderModeAllowsAppNavigation } from "@/ui/finder/finderMode";
import { usePathname, useRouter } from "expo-router";
import {
  CalendarBlankIcon,
  FirstAidIcon,
  HouseIcon,
  MapPinIcon,
  MoonIcon,
  PawPrintIcon,
  SunIcon,
  UserCircleIcon,
  UsersThreeIcon,
} from "phosphor-react-native";
import React from "react";
import { Pressable, Text, View } from "react-native";

/**
 * App-Shell Navigation: Control Board, Profil, Theme.
 * Abmelden nur im Profil, nicht noch einmal hier.
 * Tenant-/Tier-Navigation folgt in späteren Paketen.
 */
export default function AppDrawerContent(props: DrawerContentComponentProps) {
  const { theme, mode, setMode } = useTheme();
  const s = useStyles();
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();
  const finderMode = useOptionalFinderMode();
  const navAllowed = finderModeAllowsAppNavigation(!!finderMode?.active);

  const go = (href: string) => {
    if (!navAllowed) return;
    router.push(href as any);
    props.navigation.dispatch(DrawerActions.closeDrawer());
  };

  const Item = ({
    label,
    href,
    icon,
    active,
  }: {
    label: string;
    href: string;
    icon: React.ReactNode;
    active: boolean;
  }) => (
    <Pressable
      onPress={() => go(href)}
      style={active ? s.drawerItemActive : s.drawerItem}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {icon}
      <Text style={[s.textBody, { fontWeight: active ? "700" : "600", flex: 1 }]}>
        {label}
      </Text>
    </Pressable>
  );

  const isHome = pathname === "/home" || pathname === "/";
  const isCareSites =
    pathname === "/my/care-sites" || pathname.startsWith("/my/care-sites/");
  const isAnimals =
    pathname === "/my/animals" || pathname.startsWith("/my/animals/");
  const isMedicus =
    pathname === "/my/medicus" || pathname.startsWith("/my/medicus/");
  const isClub = isVereinAreaPath(pathname);
  const isCalendar =
    pathname === "/my/calendar" || pathname.startsWith("/my/calendar/");
  const isProfile =
    pathname === "/my/profile" || pathname.startsWith("/my/profile/");

  return (
    <DrawerContentScrollView
      {...props}
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: theme.space.xl,
        backgroundColor: theme.color.panelContentBg,
      }}
    >
      <View style={{ paddingHorizontal: theme.space.md, gap: theme.space.md }}>
        <View style={{ alignItems: "flex-start", paddingVertical: theme.space.sm }}>
          <LogoFerinaCura width={200} height={70} fill={theme.color.primary} />
        </View>

        <Text style={s.drawerSection}>Arbeitsplatz</Text>
        <Item
          label="Control Board"
          href="/home"
          active={isHome}
          icon={
            <HouseIcon
              size={20}
              color={isHome ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Tiere"
          href="/my/animals"
          active={isAnimals}
          icon={
            <PawPrintIcon
              size={20}
              color={isAnimals ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Pflegestellen"
          href="/my/care-sites"
          active={isCareSites}
          icon={
            <MapPinIcon
              size={20}
              color={isCareSites ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Medikus"
          href="/my/medicus"
          active={isMedicus}
          icon={
            <FirstAidIcon
              size={20}
              color={isMedicus ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Verein"
          href="/my/club"
          active={isClub}
          icon={
            <UsersThreeIcon
              size={20}
              color={isClub ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Kalender"
          href="/my/calendar"
          active={isCalendar}
          icon={
            <CalendarBlankIcon
              size={20}
              color={isCalendar ? theme.color.primary : theme.color.text}
            />
          }
        />
        <Item
          label="Profil"
          href="/my/profile"
          active={isProfile}
          icon={
            <UserCircleIcon
              size={20}
              color={isProfile ? theme.color.primary : theme.color.text}
            />
          }
        />

        <Text style={[s.drawerSection, { marginTop: theme.space.md }]}>Darstellung</Text>
        <Pressable
          onPress={() => {
            if (!navAllowed) return;
            setMode(mode === "light" ? "dark" : "light");
          }}
          style={s.drawerItem}
          accessibilityRole="button"
          accessibilityLabel="Hell/Dunkel umschalten"
        >
          {mode === "light" ? (
            <MoonIcon size={20} color={theme.color.text} />
          ) : (
            <SunIcon size={20} color={theme.color.text} />
          )}
          <Text style={[s.textBody, { fontWeight: "600", flex: 1 }]}>
            {mode === "light" ? "Dunkler Modus" : "Heller Modus"}
          </Text>
        </Pressable>

        <View style={{ marginTop: theme.space.lg, gap: theme.space.xs }}>
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}>
            {user?.email ?? ""}
          </Text>
        </View>
      </View>
    </DrawerContentScrollView>
  );
}
